import { EDGE_TAB_NOTCH_PX } from '@feedback/shared';
import { BRAND_ACCENT, BRAND_FONT_STACK } from './brand';
import { PHONE_DOCK_GAP_PX, PHONE_DOCK_RIGHT_PX, PHONE_DOCK_SIZE_PX } from './phone-dock';

/**
 * The edge tab, from the I22565 reference stylesheet.
 *
 * Every length and colour is a custom property on `.ft-root`. Host-owned
 * properties (`--fb-lane-h`, `--fb-nav-h`) are not given a local value here:
 * they inherit from the host document into this shadow tree. `--toast-lift`
 * is published for the toast. The tab does not read it, or the tab would rise
 * by its own height.
 *
 * Tab rules only. The tab opens the same centered modal as the button
 * (I22642), so nothing here may style `.ft-overlay` or `.ft-dialog`: this
 * sheet is injected after `styles.ts`, and a rule here would quietly win over
 * the modal's own.
 *
 * The focus indicator is the same parallelogram, drawn as two layers on the
 * unclipped frame: a 2px ring and, inside it, a 2px gap. A rectangular
 * outline would not follow the slant. The button's own outline stays
 * `transparent`, not `none`: the ring is relocated onto the frame, and the
 * brand guard forbids suppressing it (ADR 0006).
 */
export const edgeTabStyles = `
.ft-root {
  --fb-w: 36px;
  --fb-h: 120px;
  --fb-slope: 0.21256;
  --fb-bleed: 9px;
  --fb-rise-box: calc((var(--fb-w) + var(--fb-bleed)) * var(--fb-slope));
  --fb-hit: 44px;
  --fb-icon: 16px;
  --fb-font: 600 12.5px/1 ${BRAND_FONT_STACK};
  --fb-ls: 0.02em;
  --fb-right: 0px;
  --fb-gap-bottom: 16px;
  --fb-bottom: calc(var(--fb-lane-h, 0px) + var(--fb-gap-bottom));
  --fb-gutter: 48px;
  --fb-z: 9000;
  --fb-fill: #041c74;
  --fb-ink: #ffffff;
  --fb-fill-hover: #3444e3;
  --fb-fill-pressed: #040c4c;
  --fb-fill-open: #040c4c;
  --fb-open-bar: #65cc48;
  --fb-ring: #3444e3;
  --fb-ring-gap: #ffffff;
  --fb-nudge: 4px;
  --fb-press: 2px;
  --fb-dur-hover: 160ms;
  --fb-dur-in: 320ms;
  --fb-delay-in: 400ms;
  --fb-dur-panel: 240ms;
  --fb-ease: cubic-bezier(.16, 1, .3, 1);
  --fb-ease-in: cubic-bezier(.7, 0, .84, 0);
  --fb-top-clear: 72px;
  color: var(--fb-ink);
  font-family: ${BRAND_FONT_STACK};
}
.ft-root[data-theme="dark"] {
  --fb-fill: #1d94ff;
  --fb-ink: #040c4c;
  --fb-fill-hover: #5ab0ff;
  --fb-fill-pressed: #8cc8ff;
  --fb-fill-open: #f3f5fa;
  --fb-open-bar: #3444e3;
  --fb-ring: #1d94ff;
  --fb-ring-gap: #040c4c;
}
.ft-tab {
  position: fixed;
  right: 0;
  bottom: var(--fb-bottom);
  z-index: var(--fb-z);
  width: var(--fb-hit);
  height: var(--fb-h);
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--fb-ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  font-family: ${BRAND_FONT_STACK};
  transition: transform var(--fb-dur-panel) var(--fb-ease-in);
}
.ft-tab:focus {
  outline: 2px solid transparent;
}
.ft-tab:focus-visible {
  outline: 2px solid transparent;
  outline-offset: 2px;
}
.ft-tab__frame {
  position: absolute;
  top: 0;
  bottom: 0;
  right: calc(var(--fb-bleed) * -1);
  width: calc(var(--fb-w) + var(--fb-bleed));
  transition: transform var(--fb-dur-hover) var(--fb-ease);
}
/*
 * Same polygon as the shape, on a box 4px and 2px larger. \`--fb-slope\` is
 * tan 12deg, and the rise has to be that slope times the BOX WIDTH. A \`100%\`
 * in the y of a clip-path is a percentage of the height, which made the ring
 * about twice as steep as the tab and hid it under the shape. The outer layer
 * is the ring; the inner layer is the gap. The shape paints over both.
 */
.ft-tab__frame::before,
.ft-tab__frame::after {
  content: "";
  position: absolute;
  pointer-events: none;
  opacity: 0;
}
.ft-tab__frame::before {
  inset: -4px;
  z-index: 0;
  background: var(--fb-ring);
  --fb-ring-rise: calc((var(--fb-w) + var(--fb-bleed) + 8px) * var(--fb-slope));
  clip-path: polygon(0 var(--fb-ring-rise), 100% 0, 100% calc(100% - var(--fb-ring-rise)), 0 100%);
}
.ft-tab__frame::after {
  inset: -2px;
  z-index: 1;
  background: var(--fb-ring-gap);
  --fb-ring-rise: calc((var(--fb-w) + var(--fb-bleed) + 4px) * var(--fb-slope));
  clip-path: polygon(0 var(--fb-ring-rise), 100% 0, 100% calc(100% - var(--fb-ring-rise)), 0 100%);
}
.ft-tab__shape {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: var(--fb-fill);
  color: var(--fb-ink);
  clip-path: polygon(0 var(--fb-rise-box), 100% 0, 100% calc(100% - var(--fb-rise-box)), 0 100%);
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-right: var(--fb-bleed);
  transition: background-color var(--fb-dur-hover) linear;
}
/*
 * The icon takes its own ink, as the button's does: \`--fb-icon-ink\` is the
 * application's icon color, falling back to the label ink. A chosen preset or
 * image arrives drawn in \`currentColor\` (\`renderLauncherIcon\`), so this one
 * declaration colors the tab's glyph and an application's icon alike.
 */
.ft-tab__icon {
  width: var(--fb-icon);
  height: var(--fb-icon);
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fb-icon-ink, currentColor);
}
.ft-tab__icon svg,
.ft-tab__icon img {
  width: var(--fb-icon);
  height: var(--fb-icon);
  display: block;
}
.ft-tab__icon svg {
  fill: currentColor;
}
.ft-tab__label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font: var(--fb-font);
  letter-spacing: var(--fb-ls);
  white-space: nowrap;
  margin-top: 2px;
}
.ft-tab__shape::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--fb-open-bar);
  transform: scaleY(0);
  transition: transform var(--fb-dur-hover) var(--fb-ease);
}
@media (hover: hover) and (pointer: fine) {
  .ft-tab:hover .ft-tab__frame,
  .ft-tab.is-hover .ft-tab__frame {
    transform: translateX(calc(var(--fb-nudge) * -1));
  }
  .ft-tab:hover .ft-tab__shape,
  .ft-tab.is-hover .ft-tab__shape {
    background: var(--fb-fill-hover);
    color: var(--fb-ink-hover, var(--fb-ink));
  }
  .ft-tab:hover .ft-tab__icon,
  .ft-tab.is-hover .ft-tab__icon {
    color: var(--fb-icon-ink-hover, var(--fb-icon-ink, currentColor));
  }
  .ft-tab:hover .ft-tab__tip {
    opacity: 1;
  }
}
.ft-tab:active .ft-tab__frame,
.ft-tab.is-pressed .ft-tab__frame {
  transform: translateX(calc(var(--fb-press) * -1));
  transition-duration: 60ms;
}
.ft-tab:active .ft-tab__shape,
.ft-tab.is-pressed .ft-tab__shape {
  background: var(--fb-fill-pressed);
}
.ft-tab[aria-expanded="true"] .ft-tab__shape {
  background: var(--fb-fill-open);
}
.ft-tab[aria-expanded="true"] .ft-tab__shape::before {
  transform: scaleY(1);
}
.ft-tab:focus-visible .ft-tab__frame::before,
.ft-tab:focus-visible .ft-tab__frame::after,
.ft-tab.is-focus .ft-tab__frame::before,
.ft-tab.is-focus .ft-tab__frame::after {
  opacity: 1;
}
.ft-tab.is-entering .ft-tab__frame {
  animation: ft-tab-in var(--fb-dur-in) var(--fb-ease) var(--fb-delay-in) both;
}
@keyframes ft-tab-in {
  from { transform: translateX(100%); }
}
.ft-tab.is-hidden {
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms linear;
}
/*
 * The application's shape decides the tab's ends (I22656, ADR 0036). The
 * slanted tab above is PARALLELOGRAM. The others drop the slant, and their
 * focus ring with it: the two ring layers become plain boxes 2px and 4px
 * outside the shape, which follows a square end, a notch, a radius or a mask
 * closely enough to read as the tab's own ring.
 */
.ft-tab--square .ft-tab__shape,
.ft-tab--compact .ft-tab__shape {
  clip-path: none;
}
.ft-tab--notch .ft-tab__shape {
  clip-path: polygon(${EDGE_TAB_NOTCH_PX}px 0, 100% 0, 100% 100%, 0 100%, 0 ${EDGE_TAB_NOTCH_PX}px);
}
.ft-tab--square .ft-tab__frame::before,
.ft-tab--square .ft-tab__frame::after,
.ft-tab--notch .ft-tab__frame::before,
.ft-tab--notch .ft-tab__frame::after,
.ft-tab--compact .ft-tab__frame::before,
.ft-tab--compact .ft-tab__frame::after {
  clip-path: none;
}
/* An icon-only tab does not need 120px of bar for a 16px glyph. */
.ft-tab--icon-only {
  --fb-h: 56px;
}
/*
 * SQUARE, TEARDROP and CUSTOM have a 1:1 aspect, so the tab becomes a 44px
 * tile flush to the edge. No bleed: a mask stretched into a hidden strip would
 * lose a fifth of its silhouette off-screen.
 */
.ft-tab--compact {
  --fb-w: 44px;
  --fb-h: 44px;
  --fb-bleed: 0px;
}
.ft-tab--compact .ft-tab__shape {
  padding-right: 0;
}
/*
 * The tooltip opens to the left, the only side with room. Same paint as the
 * button's tooltip (\`Button.tsx\`), and shown on hover or keyboard focus.
 */
.ft-tab__tip {
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 10px;
  background: ${BRAND_ACCENT};
  color: #ffffff;
  font: 600 12px/1.3 ${BRAND_FONT_STACK};
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms linear;
}
.ft-tab:focus-visible .ft-tab__tip {
  opacity: 1;
}
.ft-tab.is-hidden .ft-tab__tip {
  opacity: 0;
}
.ft-tab[aria-expanded="true"] .ft-tab__frame {
  transform: none;
}
.ft-root.is-suppressed .ft-tab,
.ft-root.is-suppressed .ft-launcher {
  opacity: 0;
  pointer-events: none;
}
.ft-dock {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: var(--fb-fill, #041c74);
  color: var(--fb-ink, #ffffff);
  cursor: pointer;
  font-family: ${BRAND_FONT_STACK};
}
.ft-dock svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}
.ft-dock:focus-visible {
  /* Sky, after a 2px gap. The gap is the masthead's own Catalina, so the
   * offset is what paints the #041C74 ring-gap. Royal is the tab's ring;
   * this control sits on the navy bar, where Sky is the focus colour. */
  outline: 2px solid #1d94ff;
  outline-offset: 2px;
}
/*
 * Admin-selected phone option B. The trigger lives in this shadow tree, so
 * these rules reach it. \`mountTrigger\` cannot: that button is appended to a
 * host element in the light DOM.
 *
 * It sits above the host bar, not in it. A 44px control centered in the bar
 * covered the last item (the sample host's More read Mo). \`--fb-nav-h\` is
 * the height the host already declares for the edge tab. The gap matches
 * \`--fb-bottom\`. With no bar the variable is 0 and the trigger sits one gap
 * above the safe area, still right-aligned. z-index keeps it above the phone
 * sheet so it remains the close control.
 */
.ft-phone-dock {
  position: fixed;
  right: calc(env(safe-area-inset-right, 0px) + ${PHONE_DOCK_RIGHT_PX}px);
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--fb-nav-h, 0px) + ${PHONE_DOCK_GAP_PX}px);
  z-index: calc(var(--fb-z, 9000) + 2);
  width: ${PHONE_DOCK_SIZE_PX}px;
  height: ${PHONE_DOCK_SIZE_PX}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: var(--fb-fill, #041c74);
  color: var(--fb-ink, #ffffff);
  cursor: pointer;
  font-family: ${BRAND_FONT_STACK};
  outline: 2px solid transparent;
  outline-offset: 2px;
}
.ft-phone-dock svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}
.ft-phone-dock:focus-visible {
  outline-color: #1d94ff;
}
.ft-phone-dock svg,
.ft-phone-dock img {
  display: block;
}
.ft-phone-dock .ft-tab__icon {
  color: var(--fb-icon-ink, currentColor);
}
.ft-phone-dock.is-hidden {
  opacity: 0;
  pointer-events: none;
}
@media (max-width: 599.98px) {
  .ft-root {
    --fb-w: 27px;
    --fb-h: 56px;
    --fb-bleed: 9px;
    --fb-icon: 16px;
    --fb-nudge: 0px;
    --fb-bottom: calc(env(safe-area-inset-bottom, 0px) + var(--fb-nav-h, 0px) + 12px);
  }
  .ft-tab__label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  .ft-tab {
    transition: bottom 200ms var(--fb-ease);
  }
  .ft-tab[aria-expanded="true"] {
    transform: none;
    opacity: 0;
    pointer-events: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ft-tab__frame,
  .ft-tab__shape,
  .ft-tab__shape::before {
    transition: none !important;
  }
  .ft-tab:hover .ft-tab__frame,
  .ft-tab.is-hover .ft-tab__frame,
  .ft-tab:active .ft-tab__frame,
  .ft-tab.is-pressed .ft-tab__frame,
  .ft-tab[aria-expanded="true"] .ft-tab__frame {
    transform: none;
  }
  .ft-tab.is-entering .ft-tab__frame {
    animation: ft-tab-fade 120ms linear var(--fb-delay-in) both;
  }
  .ft-tab,
  .ft-tab[aria-expanded="true"] {
    transition: none !important;
  }
}
@keyframes ft-tab-fade {
  from { opacity: 0; }
}
`;
